<template>
  <div
    class="container flex md:mx-auto mx-0 flex-col md:flex-row mt-6"
    :style="`height: 80vh;`"
  >
    <!-- Left side - active integrations -->
    <div class="md:w-1/2 bg-white rounded md:m-4 md:p-4 ml-4 mr-4 p-2 mb-10">
      <!-- title -->
      <div class="font-bold mb-5">
        {{ $t("activeIntegrations.table.title") }}
      </div>

      <!-- active integrations -->
      <div v-if="activeIntegrations.length">
        <div v-for="(integration, i) in activeIntegrations" :key="i">
          <rele-integration
            :integration="integration"
            @on-edit="onEditIntegration"
            @on-delete="onDeleteIntegration"
          />
        </div>
      </div>
      <!-- No data icon - if there is no active integrations -->
      <div
        v-else
        class="flex flex-col items-center h-full ml-10 mr-10 mt-5 mb-5"
      >
        <div class="md:mt-40 flex flex-col items-center">
          <img src="@/assets/icons/no-data.svg" class="w-20" />
          <div
            class="mt-4 text-gray-500 text-center"
            v-html="$t('activeIntegrations.table.noIntegrations')"
          />
        </div>
      </div>
    </div>

    <!-- Right side - non-active integrations  -->
    <div
      class="md:w-1/2 bg-white rounded md:m-4 md:p-4 ml-4 mr-4 p-2 md:pb-0 pb-20"
    >
      <!-- title -->
      <div class="font-bold mb-5">
        {{ $t("nonActiveIntegrations.title") }}
      </div>

      <!-- Description -->
      <div
        v-if="nonActiveIntegrations.length"
        class="text-center text-lg m-10 md:ml-30 md:mr-30 ml-10 mr-10"
      >
        {{ $t("nonActiveIntegrations.description") }}
      </div>

      <!-- Icons -->
      <div
        v-if="nonActiveIntegrations.length"
        class="flex flex-row flex-wrap content-center justify-center mt-10 gap-5 md:gap-0"
      >
        <!-- Non active integrations -->
        <div v-for="(integration, i) in nonActiveIntegrations" :key="i">
          <!-- Load Card icon -->
          <div class="border border-solid rounded-lg md:m-3 w-40 h-full">
            <div class="divide-y divide-gray-400 flex flex-col text-center">
              <div
                class="flex justify-center content-center hover:bg-gray-200 cursor-pointer"
                @click="onCreateIntegration(integration.name)"
              >
                <img
                  class="m-6 h-20 w-20"
                  :src="getImageUrl(integration.name)"
                />
              </div>
              <div class="px-6 py-4">
                <!--  -->
                <div class="font-bold">
                  {{ $t(`nonActiveIntegrations.${integration.name}.title`) }}
                </div>
                <p class="text-gray-700 text-sm mt-4">
                  {{
                    $t(`nonActiveIntegrations.${integration.name}.description`)
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Icon if all integrations active -->
      <div
        v-else
        class="flex flex-row flex-wrap content-center justify-center mt-10 gap-5 md:gap-0"
      >
        <div class="md:mt-40 flex flex-col items-center">
          <img src="@/assets/icons/no-integration-to-add.png" class="w-20" />
          <div
            class="mt-4 text-gray-500 text-center"
            v-html="$t('nonActiveIntegrations.noIntegrations')"
          />
        </div>
      </div>
    </div>

    <!-- Dialogs -->
    <!-- Connection integration Dialog -->
    <rele-connection-dialog
      v-model="integrationDialog.open"
      :type="integrationDialog.type"
      :mode="integrationDialog.mode"
      :authType="integrationDialog.authType"
      @on-close="onCloseDialog"
      @on-api-key="onApiKeyClick"
      @on-oauth="onOauthClick"
      ref="releConnectionDialog"
    />

    <!-- Delete integration Dialog -->
    <!-- Delete dialog -->
    <r-dialog id="delete-dialog" v-model="deleteDialog.open">
      <div class="flex flex-col bg-white h-full w-full rounded shadow-lg p-4">
        <!-- title -->
        <div class="font-bold">
          {{ $t("integrations.crm.deleteDialog.title") }}
        </div>

        <!-- form -->
        <div class="h-full flex flex-col justify-center md:py-8 mt-10 md:mt-0">
          {{
            $t("integrations.crm.deleteDialog.description", {
              name: this.deleteDialog.type
            })
          }}
        </div>

        <!-- buttons -->
        <div class="flex flex-row w-full justify-end mt-10 md:mt-0">
          <!-- cancel -->
          <r-button
            id="user-close-dialog"
            class="mr-4"
            text
            color="black"
            @click="toggleDeleteDialog"
          >
            {{ $t("buttons.cancel") }}
          </r-button>

          <!-- done -->
          <r-button
            id="user-save-dialog"
            @click="deleteIntegration"
            color="error"
            :loading="deleteDialog.loading"
          >
            {{ $t("buttons.delete") }}
          </r-button>
        </div>
      </div>
    </r-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex"

export default {
  // local state
  data() {
    return {
      // integration dialog
      integrationDialog: {
        open: false,
        type: "",
        mode: "create",
        authType: ""
      },

      // delete dialog
      deleteDialog: {
        open: false,
        type: "",
        loading: false
      },

      integrations: [
        {
          name: "hubspot",
          apiKey: "",
          isActive: false
        },
        {
          name: "salesforce",
          isActive: false
        },
        {
          name: "mailing",
          isActive: false
        },
        {
          name: "zapier",
          isActive: false
        }
      ]
    }
  },

  // computed props
  computed: {
    ...mapGetters({
      // map attributes
      jwt: "auth/getIdToken",

      userDoc: "users/getDoc",

      // org attribute
      org: "orgs/getDoc"
    }),
    /**
     * Returns the notification height
     */
    notificationHeight() {
      if (!process.server && document) {
        const elem = document.getElementById("global-notification-bar")
        if (elem) {
          return elem.clientHeight + "px"
        }
      }

      return "0px"
    },

    /**
     * Returns all active integrations
     */
    activeIntegrations() {
      return this.integrations.filter((integration) => {
        return integration.isActive
      })
    },

    /**
     * Returns non-active integrations
     */
    nonActiveIntegrations() {
      return this.integrations.filter((integration) => {
        return !integration.isActive && integration.name !== "zapier"
      })
    },

    /**
     * isHubspotAndApiKey
     */
    isHubspotAndApiKey() {
      return this.deleteDialog.type === "hubspot" && this.org?.hubspot?.api_key
    }
  },

  // watch properties
  watch: {
    // monitor the user doc
    // update integrations array when props arrived
    userDoc: {
      immediate: true,
      handler() {
        if (((this.userDoc || {}).orgs || []).length) {
          const orgId = this.userDoc.orgs[0]
          this.integrations = this.integrations.map((integration) => {
            const isActive = Boolean(
              (this.userDoc[integration.name] || {})[orgId]
            )

            return {
              ...integration,
              isActive
            }
          })
        }
      }
    },

    // montior org prop
    // update integrations array when props arrived
    org: {
      immediate: true,
      handler() {
        if (this.org?.hubspot?.api_key) {
          this.integrations = this.integrations.map((integration) => {
            if (integration.name === "hubspot") {
              return {
                ...integration,
                isActive: true,
                apiKey: this.org?.hubspot?.api_key
              }
            } else return integration
          })
        }

        if (Object.keys(this.org?.zapier || {}).length) {
          this.integrations = this.integrations.map((integration) => {
            if (integration.name === "zapier") {
              return {
                ...integration,
                isActive: true
              }
            } else return integration
          })
        }
      }
    }
  },

  // handle notification
  notifications: {
    notifyError: {
      type: "error"
    }
  },

  // methods
  methods: {
    /**
     * Returns image url
     */
    getImageUrl(name) {
      let image = ""
      if (name === "hubspot") {
        image = "hubspot-mini"
      } else if (name === "salesforce") {
        image = "salesforce-mini"
      } else {
        image = "mailing"
      }
      return require(`@/assets/icons/${image}.png`)
    },

    /**
     * On edit integration
     */
    onEditIntegration(type) {
      window.scrollTo(0, 0)
      const authType = this.org?.hubspot?.api_key ? "apiKey" : "oauth"
      this.$set(this.integrationDialog, "authType", authType)
      this.$set(this.integrationDialog, "mode", "edit")
      this.$set(this.integrationDialog, "type", type)
      this.$set(this.integrationDialog, "open", true)
    },

    /**
     * On Create integrations
     */
    onCreateIntegration(type) {
      window.scrollTo(0, 0)
      this.$set(this.integrationDialog, "mode", "create")
      this.$set(this.integrationDialog, "type", type)
      this.$set(this.integrationDialog, "open", true)
    },

    /**
     * On Delete integration
     */
    onDeleteIntegration(integrationType) {
      this.toggleDeleteDialog()
      this.$set(this.deleteDialog, "type", integrationType)
    },

    /**
     * Toggle delete dialog
     */
    toggleDeleteDialog() {
      window.scrollTo(0, 0)
      this.$set(this.deleteDialog, "open", !this.deleteDialog.open)
      this.$set(this.deleteDialog, "type", "")
    },

    /**
     * On Api key choose
     */
    onApiKeyClick() {
      this.$set(this.integrationDialog, "authType", "apiKey")
      // this.authType = "apiKey"
    },

    /**
     * On Oauth
     */
    onOauthClick() {
      this.$set(this.integrationDialog, "authType", "oauth")
    },

    /**
     * On close integration dialog
     */
    onCloseDialog() {
      this.$set(this.integrationDialog, "authType", "")
    },

    /**
     * Delete Integration
     */
    async deleteIntegration() {
      try {
        if (this.isHubspotAndApiKey) {
          await this.deleteFromOrg()
        } else {
          await this.deleteFromUser()
        }
      } catch (error) {
        this.notifyError({
          title: this.$t("errors.unableToDeleteIntegration"),
          message: error.message
        })
      }
    },

    /**
     * delete api key integration from org
     */
    async deleteFromOrg() {
      this.$set(this.deleteDialog, "loading", true)

      await this.$store.dispatch("users/removeIntegration", {
        data: {
          userId: this.userDoc.id,
          orgId: this.userDoc.orgs[0],
          integrationType: this.deleteDialog.type,
          integrationLevel: "orgs"
        }
      })

      this.$set(this.deleteDialog, "loading", false)
      this.toggleDeleteDialog()
      location.reload()
    },

    /**
     * delete oauth integration from user
     */
    async deleteFromUser() {
      this.$set(this.deleteDialog, "loading", true)
      const orgId = this.userDoc.orgs[0]

      await this.$store.dispatch("users/removeIntegration", {
        data: {
          orgId,
          userId: this.userDoc.id,
          integrationType: this.deleteDialog.type,
          integrationLevel: "users"
        }
      })

      // pull rele connection ref
      const { $refs = {} } = this.$refs?.releConnectionDialog

      // clean emails inputs
      if (this.deleteDialog.type === "mailing" && Object.keys($refs).length) {
        $refs.releIntegrationComponent.resetEmails()
      }

      // reset loader and dialog
      this.$set(this.deleteDialog, "loading", false)
      this.toggleDeleteDialog()
    }
  }
}
</script>
